import { FetchError } from '@/data/fetcher';
import {
  AlertAction,
  AlertActionTarget,
  AlertAggregation,
  AlertAttribute,
  AlertBuilder,
  AlertBuilderBanner,
  AlertRule,
  AlertRuleWithActions,
  CreateAlertRequest,
  UpdateAlertRequest,
} from '@/types/alerts';
import { CustomChartMetric } from '@/types/schema';

import { FilterLabel } from '../FilterBar/constants';
import {
  astToString,
  flattenAst,
  isSameComparison,
  stringToAst,
} from '../RunsTable/filter/ast';
import {
  INITIAL_LATENCY_FILTER,
  INITIAL_RUN_COUNT_FILTER,
  INITIAL_STATE,
} from './useAlertState';

export const BAD_ALERT_CONFIGURATION_ERROR_MESSAGE =
  'Something went wrong. Double-check your configuration and try again.';

export const GENERIC_ALERT_ERROR_MESSAGE =
  'Something went wrong. Please try again.';

export const TEST_ALERT_NOTIFICATION_SUCCESS_MESSAGE =
  'Your configuration is working correctly and the test notification was delivered.';

export const getErrorBanner = (
  title: string,
  err: Error
): AlertBuilderBanner => {
  if (err instanceof FetchError && err.status === 400) {
    return {
      title: title,
      status: 'error',
      msg: BAD_ALERT_CONFIGURATION_ERROR_MESSAGE,
    };
  }
  return {
    title: title,
    status: 'error',
    msg: GENERIC_ALERT_ERROR_MESSAGE,
  };
};

export const buildRequest = (
  alertBuilder: AlertBuilder
): CreateAlertRequest | UpdateAlertRequest | Error => {
  const convertedThreshold = parseFloat(alertBuilder.threshold);
  if (isNaN(convertedThreshold)) {
    // should never get here as buttons are disabled if threshold is invalid
    return new Error('Threshold must be a number');
  }

  const attribute = getAttributeForBackwardsCompat(alertBuilder.attribute);
  const filter = alertBuilder.filter.filter ?? '';

  const rule: AlertRule = {
    id: alertBuilder.id,
    name: alertBuilder.name,
    description: getDescription(alertBuilder),
    type: alertBuilder.type,
    attribute: attribute,
    aggregation: alertBuilder.aggregation,
    window_minutes: alertBuilder.windowMinutes === '15' ? 15 : 5,
    operator: alertBuilder.operator,
    threshold: convertedThreshold,
    filter:
      attribute === AlertAttribute.feedback_score
        ? `eq(feedback_key, "${alertBuilder.feedbackKey}")`
        : filter,
    threshold_multiplier: null,
    threshold_window_minutes: null,
    denominator_filter:
      alertBuilder.aggregation === 'pct' &&
      (attribute === AlertAttribute.run_count ||
        attribute === AlertAttribute.error_count)
        ? getDenominatorFilter(filter)
        : null,
  };

  const actions: AlertAction[] = [];
  if (
    alertBuilder.pagerDutyConfig?.severity &&
    (alertBuilder.pagerDutyConfig?.integration_key ||
      alertBuilder.pagerDutyConfig?.secret_name)
  ) {
    actions.push({
      id: alertBuilder.pagerDutyConfig.id,
      alert_rule_id: alertBuilder.id,
      target: AlertActionTarget.pagerduty,
      config: JSON.stringify(alertBuilder.pagerDutyConfig),
    });
  }
  if (
    alertBuilder.webhookConfig?.url &&
    alertBuilder.webhookConfig?.project_name
  ) {
    actions.push({
      id: alertBuilder.webhookConfig.id,
      alert_rule_id: alertBuilder.id,
      target: AlertActionTarget.webhook,
      config: JSON.stringify({
        ...alertBuilder.webhookConfig,
        headers: alertBuilder.webhookConfig?.headers || '{}',
        body: alertBuilder.webhookConfig?.body || '{}',
        method: 'POST',
      }),
    });
  }
  return { rule, actions };
};

export const getDescription = (alertBuilder: AlertBuilder): string => {
  if (
    alertBuilder.attribute === 'run_count' ||
    alertBuilder.attribute === 'error_count'
  ) {
    if (alertBuilder.aggregation === 'pct') {
      return `Error rate > ${alertBuilder.threshold}% in the last ${alertBuilder.windowMinutes} minutes`;
    }
    return `Errored run count > ${alertBuilder.threshold} in the last ${alertBuilder.windowMinutes} minutes`;
  } else if (
    alertBuilder.attribute === 'latency' ||
    alertBuilder.attribute === 'run_latency'
  ) {
    return `Average run latency > ${alertBuilder.threshold} ${
      alertBuilder.threshold === '1' ? 'second' : 'seconds'
    } in the last ${alertBuilder.windowMinutes} minutes`;
  }

  return `${alertBuilder.feedbackKey} ${
    alertBuilder.operator === 'gte' ? '>=' : '<='
  } ${alertBuilder.threshold} in the last ${
    alertBuilder.windowMinutes
  } minutes`;
};

export const getAlertBuilder = (
  alert: AlertRuleWithActions,
  projectName?: string
): AlertBuilder => {
  const pagerDutyConfig = alert.actions.find(
    (p) => p.target === AlertActionTarget.pagerduty
  );
  const webhookConfig = alert.actions.find(
    (p) => p.target === AlertActionTarget.webhook
  );
  const parsedPagerDutyConfig = pagerDutyConfig
    ? JSON.parse(pagerDutyConfig.config)
    : undefined;
  const parsedWebhookConfig = webhookConfig
    ? JSON.parse(webhookConfig.config)
    : undefined;

  const attribute = getAttributeForBackwardsCompat(alert.rule.attribute);

  return {
    ...INITIAL_STATE,
    id: alert.rule.id,
    name: alert.rule.name,
    description: alert.rule.description,
    type: alert.rule.type,
    attribute: attribute,
    aggregation: alert.rule.aggregation,
    threshold: alert.rule.threshold ? alert.rule.threshold.toString() : '',
    windowMinutes: alert.rule.window_minutes.toString() as '5' | '15',
    operator: alert.rule.operator,
    feedbackKey:
      attribute === AlertAttribute.feedback_score
        ? parseFeedbackKey(alert.rule.filter) ?? ''
        : '',
    filter:
      attribute === AlertAttribute.feedback_score
        ? {}
        : { filter: alert.rule.filter ?? getChartFilter(attribute) },
    actionTarget: pagerDutyConfig
      ? AlertActionTarget.pagerduty
      : AlertActionTarget.webhook,
    pagerDutyConfig: {
      id: pagerDutyConfig?.id,
      severity: parsedPagerDutyConfig?.severity,
      integration_key: parsedPagerDutyConfig?.integration_key,
      secret_name: parsedPagerDutyConfig?.secret_name,
    },
    webhookConfig: {
      id: webhookConfig?.id,
      url: parsedWebhookConfig?.url,
      headers: parsedWebhookConfig?.headers,
      body: parsedWebhookConfig?.body,
      project_name: projectName || '',
    },
  };
};

export const canUpdateOrCreateAlert = (alertBuilder: AlertBuilder): boolean => {
  const hasAlertAction = !!(
    ((alertBuilder.pagerDutyConfig?.integration_key ||
      alertBuilder.pagerDutyConfig?.secret_name) &&
      alertBuilder.pagerDutyConfig?.severity) ||
    alertBuilder.webhookConfig?.url
  );
  const hasThreshold =
    alertBuilder.attribute === AlertAttribute.feedback_score
      ? !!(alertBuilder.threshold && alertBuilder.feedbackKey)
      : !!alertBuilder.threshold;

  return (
    !!alertBuilder.name &&
    hasAlertAction &&
    hasThreshold &&
    alertBuilder.isThresholdValid
  );
};

export const validateThreshold = (value: string) => {
  const parsedThreshold = parseFloat(value);

  // Check if it's a positive decimal number and that the original string equals its string representation
  // This prevents strings like "123abc" from being considered valid
  return (
    value === '' ||
    (!isNaN(parsedThreshold) &&
      parsedThreshold.toString() === value.toString() &&
      parsedThreshold > 0)
  );
};

export const getPlaceholder = (
  attribute: AlertAttribute,
  aggregation: AlertAggregation
) => {
  if (
    attribute === AlertAttribute.error_count ||
    attribute === AlertAttribute.run_count
  ) {
    if (aggregation === 'sum') {
      return '50';
    } else if (aggregation === 'pct') {
      return '5';
    }
  } else if (attribute === AlertAttribute.feedback_score) {
    return '0.3';
  }
  return '10';
};

// handle backwards compatability for alert attribute
export const getAttributeForBackwardsCompat = (
  attribute: AlertAttribute
): AlertAttribute => {
  let newAttribute = attribute;
  if (attribute === AlertAttribute.error_count) {
    newAttribute = AlertAttribute.run_count;
  } else if (attribute === AlertAttribute.latency) {
    newAttribute = AlertAttribute.run_latency;
  }
  return newAttribute;
};

/*
 * the create alerts endpoint expects a "denominator_filter" param that
 * includes everything in filter except for status, so we use this util function
 * for popping the status from the filter
 */
export const getDenominatorFilter = (filter: string): string | null => {
  const ast = stringToAst(filter);
  const statusComparison = {
    comparator: 'eq',
    identifier: 'status',
    argument: 'error',
  };
  if (ast) {
    if ('comparator' in ast) {
      if (isSameComparison(ast, statusComparison)) {
        return null;
      }
    }
    if ('operator' in ast) {
      const filteredAst = {
        operator: ast.operator,
        operands: ast.operands.filter(
          (comparison) =>
            !('comparator' in comparison) ||
            !isSameComparison(comparison, statusComparison)
        ),
      };
      return astToString(filteredAst) || null;
    }
    return astToString(ast) || null;
  }
  return null;
};

/*
 * the backend returns feedback key as an ast within the filter param,
 * however we do not actually show the filter in the UI when the user wants to alert on feedback_key,
 * so we need to parse it and store as string
 */
export const parseFeedbackKey = (filter: string | null): string | null => {
  if (!filter) return null;
  const ast = flattenAst(stringToAst(filter));
  if (ast) {
    let match: string | null = null;
    ast.operands.forEach((op) => {
      if (op.identifier === 'feedback_key' && typeof op.argument === 'string') {
        match = op.argument;
      }
    });
    return match;
  }

  return filter;
};

export const getChartMetric = (
  attribute: AlertAttribute,
  aggregation: AlertAggregation
): CustomChartMetric => {
  if (
    attribute === AlertAttribute.latency ||
    attribute === AlertAttribute.run_latency
  ) {
    return 'latency_avg';
  } else if (attribute === AlertAttribute.feedback_score) {
    return 'feedback_score_avg';
  }
  if (aggregation === 'pct') {
    return 'error_rate';
  }
  return 'run_count';
};

export const getChartFilter = (
  attribute: AlertAttribute
): string | undefined => {
  if (
    attribute === AlertAttribute.latency ||
    attribute === AlertAttribute.run_latency
  ) {
    return INITIAL_LATENCY_FILTER;
  } else if (
    attribute === AlertAttribute.error_count ||
    attribute === AlertAttribute.run_count
  ) {
    return INITIAL_RUN_COUNT_FILTER;
  }
};

export const ALLOWED_ALERT_FILTERS = [
  FilterLabel.inputs,
  FilterLabel.outputs,
  FilterLabel.error,
  FilterLabel.input_kv,
  FilterLabel.output_kv,
  FilterLabel.metadata,
  FilterLabel.tag,
  FilterLabel.run_type,
  FilterLabel.name,
  FilterLabel.status,
  FilterLabel.latency,
  FilterLabel.is_root,
];
